import { BaseService } from './base.service';
import {
  IAppointmentParameter, IAppointment, AppointmentDto,
  IAppointmentListing, AppointmentListingViewModel
} from '../models/appointment';
import { Uris } from '../common/constants';
import { AppointmentTypes } from '../common/enums';
import { DateTimeHelper } from '../utils/DateTimeHelper.util';
import * as _ from "lodash";

export interface IAgendaService {
  getAppointmentById(id?: number, payload?: IAppointment);
  getAppointments(param: IAppointmentParameter);
  recordAppointment(appointment: IAppointment, appointmentState: 'confirmed' | 'draft');
}
export class AgendaService extends BaseService implements IAgendaService {

  /**
   *
   */
  constructor() {
    super('agenda');
  }

  /**
   * If id has value, request api to get detail of appointment, unless create new appointment and return as a promise
   * @param id Appointment ID
   * @param payload New appointment object
   */
  getAppointmentById(id?: number, payload?: any) {
    try {
      if (id) {
        const mergingObj: any = {
          start_date: payload.start_date,
          id: payload.id,
          end_date: payload.end_date,
          name: payload.name,
          description: payload.description,
          type: payload.type,
          character_problems: payload.character_problems
        }
        if (payload.type !== AppointmentTypes.Event) {
          if (payload.actions.length > 0) /** Temporary line code for updating an appointment/event */
            mergingObj.fk_ticket = payload.actions[0].channel.fk_channel_ticket;
          mergingObj.participants = payload.participants.map(x => x.id);
          mergingObj.independent_contractors = payload.independent_contractors.map(x => x.id);
          mergingObj.welcomings = payload.welcomings.map(x => x.id);
          mergingObj.appointment_state = payload.appointment_state
        } else {
          mergingObj.fk_block = payload.fk_block;
        }
        return mergingObj;
      } else {
        return new AppointmentDto(payload)
      }
    }
    catch (error) {
      throw error;
    }
  }

  /**
   * Get list appointments
   * By default get all events
   * @param param firstDay | lastDay | type="event" | "appointment"
   */
  async getAppointments(param: IAppointmentParameter) {
    return super.get(this._path, param)
      .then((data: IAppointmentListing[]) => {

        const appointmentListingViewModel = data.map((record: IAppointmentListing) =>
          this.mapAppontmentListingModelToAppointmentListingViewModel(record).next().value);

        return Promise.resolve({ originalAppointments: data, events: appointmentListingViewModel });

      })
      .catch(error => Promise.reject(error))
  }

  /**
   * Map original object to appointment listing view model for display purpose
   * @param appointmentListing Appointment model return from api
   */
  *mapAppontmentListingModelToAppointmentListingViewModel(appointmentListing: IAppointmentListing) {
    yield new AppointmentListingViewModel(appointmentListing);
  }

  /**
   * Handle both create new and edit appointment/event
   * Mapping created object to appointment listing view model
   * @param appointment appointment object
   */
  async recordAppointment(appointment: IAppointment, appointmentState: 'confirmed' | 'draft') {

    let metaData: AppointmentDto;

    if (appointment.type === AppointmentDto.APPOINTMENT_TYPE_APPOINTMENT) {
      metaData = this.initiateAppointment(appointment, appointmentState);
    } else {
      metaData = this.initiateEvent(appointment);
    }

    return super.post(Uris.Agenda, metaData)
      .then((data: IAppointmentListing) => {
        return Promise.resolve({
          originalAppointment: data,
          appointment: this.mapAppontmentListingModelToAppointmentListingViewModel(data).next().value
        })
      })
      .catch(error => {
        return Promise.reject(error)
      })
  }

  async removeAppointment(payload: { originId: string, ticketId: string }) {
    if (payload.ticketId) {
      return await super.post(Uris.TicketDeleteAppointment.replace(':id', payload.ticketId), {
        action_type: "cancel_appointment",
        appointment_id: Number(payload.originId)
      })
        .then((data: IAppointmentListing) => data)
        .catch(error => {
          return Promise.reject(error);
        })
    } else {
      return await super.delete(Uris.AgendaId.replace(':id', payload.originId))
    }

  }
  async updateAppointment(appointment: any) {

    if (appointment.start_date) appointment.start_date = DateTimeHelper.parseToUtc(appointment.start_date);
    if (appointment.end_date) appointment.end_date = DateTimeHelper.parseToUtc(appointment.end_date);

    return super.patch(Uris.AgendaId.replace(':id', appointment.id), appointment)
      .then(response => response)
      .catch(error => error)
  }
  async updateFullAppointment(appointment: IAppointment, originAppointment: IAppointment) {

    appointment.ticketId = appointment.fk_ticket;
    originAppointment.ticketId = originAppointment.fk_ticket;

    return await super.beforePatch(originAppointment, appointment)
      .then(async (dataChanged: any) => {

        if (dataChanged.start_date) {
          dataChanged.start_date = DateTimeHelper.parseToUtc(dataChanged.start_date);
        }
        if (dataChanged.end_date) {
          dataChanged.end_date = DateTimeHelper.parseToUtc(dataChanged.end_date);
        }

        delete dataChanged.fk_ticket;
        delete dataChanged.type;

        dataChanged.independent_contractors = _.uniq(dataChanged.independent_contractors)
        dataChanged.participants = _.uniq(dataChanged.participants)
        dataChanged.welcomings = _.uniq(dataChanged.welcomings)

        const appointment = await super.patch(Uris.AgendaId.replace(':id', originAppointment.id.toString()), dataChanged);



        return Promise.resolve({
          appointment: this.mapAppontmentListingModelToAppointmentListingViewModel(appointment).next().value,
          originalAppointment: appointment
        })
      })
      .catch(error => error);
  }
  initiateEvent(appointment: IAppointment) {
    return new AppointmentDto({
      type: AppointmentTypes.Event,
      name: appointment.name,
      fk_block: appointment.fk_block,
      description: appointment.description,
      start_date: DateTimeHelper.parseToUtc(appointment.start_date),
      end_date: DateTimeHelper.parseToUtc(appointment.end_date),
    })
  }
  initiateAppointment(appointment: IAppointment, appointmentState: 'confirmed' | 'draft') {
    let ticketId: number;
    if (typeof appointment.fk_ticket === 'object') {
      ticketId = appointment.fk_ticket['id'];
    }
    else {
      ticketId = appointment.fk_ticket;
    }
    return new AppointmentDto({
      name: appointment.name,
      description: appointment.description,
      start_date: DateTimeHelper.parseToUtc(appointment.start_date),
      end_date: DateTimeHelper.parseToUtc(appointment.end_date),
      type: AppointmentTypes.Appointment,
      appointment_state: appointmentState,
      ticketId: ticketId,
      independent_contractors: appointment.independent_contractors,
      participants: appointment.participants,
      welcomings: appointment.welcomings,
      fk_block: appointment.fk_block,
    })
  }
}



// WEBPACK FOOTER //
// ./src/services/agenda.service.ts